import { useCallback } from "react";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import { convertHtmlToText } from "utils/common";

export default function useCopyHtmlContentToClipboard() {
  const dispatch = useAppDispatch();

  /** Resolves `true` when the content reached the clipboard, `false` when the write was refused. */
  const handleCopyText = useCallback(
    async (value: string, displayMessage?: boolean): Promise<boolean> => {
      try {
        const plainText = convertHtmlToText(value);

        const htmlBlob = new Blob([value], { type: "text/html" });
        const textBlob = new Blob([plainText], { type: "text/plain" });
        const item = new ClipboardItem({
          "text/html": htmlBlob,
          "text/plain": textBlob
        });

        await navigator.clipboard.write([item]);
        if (displayMessage) {
          dispatch(setCustomMessage("Content copied to clipboard"));
          dispatch(setOpen(3000));
        }
        return true;
      } catch (err) {
        // `navigator.clipboard.write` is refused without focus/permission, and ClipboardItem is
        // not universally supported. Swallowing this left the user with no content and no
        // explanation, so always report it — the caller decides nothing here.
        console.error("Failed to copy text:", err);
        dispatch(
          setCustomMessage("Couldn't copy the content to your clipboard")
        );
        dispatch(setOpen(5000));
        return false;
      }
    },
    [dispatch]
  );

  return handleCopyText;
}
